/**
 * The auth store — the client's design, trimmed. The token lives in a module variable, never in a `ref`:
 * a ref puts it in devtools and in any Pinia snapshot, and nothing renders it.
 */

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

export type TokenProvider = () => Promise<string>;

let tokenProvider: TokenProvider | null = null;

export interface AuthUser {
  readonly sub: string;
  readonly email: string | null;
  readonly name: string | null;
}

export class AuthNotReadyError extends Error {
  constructor() {
    super('no token provider registered — Auth0 has not finished initialising');
    this.name = 'AuthNotReadyError';
  }
}

export const useAuthStore = defineStore('auth', () => {
  const isAuthenticated = ref(false);
  const isLoading = ref(true);
  const user = ref<AuthUser | null>(null);
  const lastError = ref<string | null>(null);

  const displayName = computed(() => user.value?.name ?? user.value?.email ?? null);

  function setProvider(provider: TokenProvider | null): void {
    tokenProvider = provider;
  }

  /** Always asks the SDK, which owns expiry and refresh — a cached copy would only be a stale one. */
  async function getAccessToken(): Promise<string> {
    if (tokenProvider === null) {
      throw new AuthNotReadyError();
    }
    return tokenProvider();
  }

  function setAuthenticated(next: boolean, nextUser: AuthUser | null): void {
    isAuthenticated.value = next;
    user.value = nextUser;
  }

  function setLoading(next: boolean): void {
    isLoading.value = next;
  }

  function setError(message: string | null): void {
    lastError.value = message;
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    lastError,
    displayName,
    setProvider,
    getAccessToken,
    setAuthenticated,
    setLoading,
    setError,
  };
});
